<template>
    <!-- Rendered by the shared <common-list> (DemoList.vue), which is used across
         the product and is therefore left untouched. It supplies the search field,
         record counter, sorting, pagination, the row checkboxes and the avatar-
         prefixed name cell — including the demo.jpg / no-image.jpg fallback when an
         employee has no photo — so none of those are built here any more.

         DemoList renders one plain text value per cell, so each cost head arrives
         as two flat columns, "Head (Frequency)" and "Head (Yearly)", already
         formatted to the company decimal setting by listRows(). -->
    <div class="manual-list-card ctc-manual-list-wrap" :class="{ 'ctc-manual-has-checkbox': canManage }">
        <common-list
            :key="`ctc-manual-report-${listKey}`"
            :employees="listRows"
            :default_column="listDefaultColumn"
            :appended_column="listAppendedColumn"
            :company_options="listCompanyOptions"
            :primary_key="primary_key"
            :checkbox="canManage ? 1 : 0"
            :is_action="canManage ? 1 : 0"
            v-on:childToParent="onListEvent"
        ></common-list>
    </div>
</template>

<script>
import axios from 'axios';
import ctcOverlay from '../js/ctcOverlay';

export default {
    mixins: [ctcOverlay],
    name: 'ManualReportList',
    props: {
        year: {
            type: [Number, String],
            default: new Date().getFullYear()
        },
        type: {
            type: String,
            default: 'compensation'
        },
        primary_key: {
            type: String,
            default: 'employee_id'
        },
        decimalPointSettings: {
            type: String,
            default: 'no'
        }
    },
    data() {
        return {
            employees: [],
            // One entry per cost head: { value, text, frequency }. Each becomes two
            // columns in listAppendedColumn.
            heads: [],
            company_options: [],
            loading: false,
            // DemoList builds its columns once, in created(), so the list is
            // remounted whenever a fresh year or statement type arrives.
            listKey: 0,
            // Last selection DemoList reported. A row click emits an empty
            // selection alongside the row id, which would otherwise wipe the
            // parent's list of employees to send letters to — see onListEvent().
            selected: []
        };
    },
    computed: {
        canManage() {
            return !!(this.$acl && this.$acl['cost-to-company'] == 2);
        },
        // Order here is the column order on screen. `name` must stay in
        // default_column: that is the only key DemoList renders with the avatar.
        listDefaultColumn() {
            return ['name', 'employee_code', 'jobposition', 'department'];
        },
        // Amounts are pre-formatted strings, so these stay 'text' rather than
        // DemoList's 'amount' type, which would re-format them with the global
        // money rules.
        listAppendedColumn() {
            const columns = [];
            this.heads.forEach(head => {
                const label = this.stringView(String(head.text || '').replace(/_/g, ' '));
                columns.push({
                    value: head.value + '__frequency',
                    text: label + ' (' + this.getFrequencyLabel(head.frequency) + ')',
                    type: 'text'
                });
                columns.push({
                    value: head.value + '__yearly',
                    text: label + ' (Yearly)',
                    type: 'text'
                });
            });
            return columns;
        },
        // Every default column needs a matching entry here or DemoList renders the
        // column but leaves its header blank, so any the server left out are added.
        // `options` is required too: the filter sidebar reads `options.length`
        // without guarding it.
        listCompanyOptions() {
            const options = (this.company_options || []).map(option => ({
                ...option,
                options: option.options || []
            }));
            const present = options.map(option => option.label);
            this.listDefaultColumn.forEach(label => {
                if (!present.includes(label)) {
                    options.push({ label: label, type: 'text', options: [] });
                }
            });
            return options;
        },
        listRows() {
            return this.employees.map(employee => {
                const row = {
                    ...employee,
                    // DemoList reads `pic` as a filename; a null would build the
                    // avatar path around the word "null" instead of falling back.
                    pic: employee.pic || '',
                    // Action ids start at 1: DemoList sends action_id 0 for a plain
                    // cell click too.
                    actions: employee[this.primary_key] > 0 ? [{ label: 'Edit', value: 1 }] : []
                };
                this.heads.forEach(head => {
                    const value = this.parseMonthlyYearly(employee[head.value]);
                    row[head.value + '__frequency'] = value.monthly;
                    row[head.value + '__yearly'] = value.yearly;
                });
                return row;
            });
        }
    },
    watch: {
        year() {
            this.getManualCtcMapping();
        },
        type() {
            this.getManualCtcMapping();
        },
        loading(newVal) {
            this.ctcOverlay(newVal);
        }
    },
    created() {
        this.getManualCtcMapping();
    },
    methods: {
        getManualCtcMapping() {
            if (this.checkNet && !this.checkNet()) return false;
            let currentObj = this;
            currentObj.loading = true;
            axios.get('/cost-to-company/manual-ctc-mapping', {
                params: {
                    year: this.year,
                    type: this.type
                }
            })
                .then(response => {
                    let keys = Object.keys(response.data.heads || {});
                    currentObj.heads = keys.map(item => ({
                        value: item,
                        text: item,
                        type: 'text',
                        frequency: response.data.heads[item] || 'monthly'
                    }));

                    currentObj.employees = response.data.employee_data || [];
                    currentObj.company_options = response.data.options || [];
                    currentObj.selected = [];
                    currentObj.listKey++;
                    currentObj.loading = false;

                    // Emit event to parent to keep parent in sync
                    currentObj.$emit('manual-data-fetched', {
                        employees: currentObj.employees,
                        appended_column: currentObj.heads,
                        company_options: currentObj.company_options
                    });
                })
                .catch(error => {
                    console.error('Error fetching manual ctc mapping:', error);
                    currentObj.loading = false;
                });
        },
        // <common-list> reports both row events (a cell click or a row-menu pick,
        // which carry the primary key) and selection changes (which carry the key
        // as 0) through the one event. The two are forwarded to the parent as they
        // arrive, except that a row event is given the standing selection back:
        // DemoList sends an empty array with it, and the parent reads that array as
        // "nothing selected" for Download / Email Letters.
        onListEvent(value) {
            const rowId = value && value[this.primary_key];
            if (!rowId) {
                this.selected = value && value['selected'] ? value['selected'] : [];
            } else {
                value['selected'] = this.selected;
            }
            this.$emit('childToParent', value);
        },
        parseMonthlyYearly(val) {
            if (val === undefined || val === null || val === '') {
                return { monthly: '-', yearly: '-' };
            }
            const str = String(val);
            // Format from backend: "yearly (monthly)" e.g. "60000 (5000)"
            const match = str.match(/^(.+?)\s*\((.+?)\)\s*$/);
            if (match) {
                const yearly  = this.formatAmount(match[1].trim());
                const monthly = this.formatAmount(match[2].trim());
                return { yearly, monthly };
            }
            // If only a single value (no parentheses), treat as yearly only
            return { monthly: '-', yearly: this.formatAmount(str.trim()) };
        },
        /**
         * Format a monetary value according to the company decimal-point setting.
         * 'yes' → 2 decimal places; 'no' (default) → rounded integer.
         * Non-numeric strings are returned as-is (e.g. '-').
         */
        formatAmount(value) {
            const num = parseFloat(String(value).replace(/,/g, ''));
            if (isNaN(num)) return value;
            if (this.decimalPointSettings === 'yes') {
                return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            return Math.round(num).toLocaleString('en-IN');
        },
        stringView(str) {
            if (!str) return '';
            const frags = String(str).split(' ');
            for (let i = 0; i < frags.length; i++) {
                frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
            }
            return frags.join(' ');
        },
        getFrequencyLabel(freq) {
            if (!freq) return 'Monthly';
            const f = String(freq).toLowerCase();
            if (f === 'quarterly') return 'Quarterly';
            if (f === 'half_yearly' || f === 'half-yearly' || f === 'half_year') return 'Half-Yearly';
            if (f === 'yearly') return 'Yearly';
            return 'Monthly';
        }
    }
};
</script>

<style scoped>
.manual-list-card {
    background: transparent;
}

/* The <common-list> table itself — header bar, sort arrows, body rows, zebra
   striping and the suppressed column picker / filter trigger — is styled in
   resources/sass/_ctc.scss under `.ctc-manual-list-wrap`, shared with the
   Employees, Annual Reports and Audit Logs lists so they render one identical
   table. Only this screen's own additions live here. */

/* The cost columns read as money, so every cell after the four default columns
   is right-aligned. That sweep also takes in any extra field columns and the row
   menu, which sit to the right of the cost columns — the menu is floated right
   anyway, and the field columns are money-shaped too. DemoList gives every
   appended cell Bootstrap's .text-left, which is !important, so these have to be
   too. Where the sweep starts depends on the checkbox column, which is only there
   when the user may edit: .ctc-manual-has-checkbox marks that. */
.ctc-manual-list-wrap:not(.ctc-manual-has-checkbox) ::v-deep .employee-list-table tbody td:nth-child(n+5) {
    text-align: right !important;
    white-space: nowrap;
}
.ctc-manual-list-wrap.ctc-manual-has-checkbox ::v-deep .employee-list-table tbody td:nth-child(n+6) {
    text-align: right !important;
    white-space: nowrap;
}

/* Row-menu icon. DemoList picks the icon from the action's label and falls back
   to a gear for anything it doesn't know, which is what "Edit" gets. It can't be
   edited, so the gear is swapped here: its paths are hidden and the Bootstrap
   `pencil-square` outline is masked over the same box, which keeps the icon's
   1em size and `currentColor` fill. */
.ctc-manual-list-wrap ::v-deep .action-dropdown-menu svg.bi-gear > * {
    display: none;
}
.ctc-manual-list-wrap ::v-deep .action-dropdown-menu svg.bi-gear {
    background-color: currentColor;
    -webkit-mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/%3E%3C/svg%3E");
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: 1em 1em;
    mask-size: 1em 1em;
}

/* Pagination is styled centrally in resources/sass/_ctc.scss. */
</style>
